import { getApiErrorDetails } from '@amglobal/api-client'
import { isAxiosError, type AxiosError } from 'axios'
import { extractApiErrorMessage } from '@/shared/api/extract-api-error-message'
import { enMessages } from '@/shared/i18n/messages/en'
import type { TranslationFn } from '@/shared/i18n/use-translation'

const defaultT: TranslationFn = (key, params) => {
  const parts = key.split('.')
  let current: unknown = enMessages
  for (const part of parts) {
    if (current === null || typeof current !== 'object' || !(part in current)) {
      return key
    }
    current = (current as Record<string, unknown>)[part]
  }
  if (typeof current !== 'string') return key
  if (!params) return current
  return Object.entries(params).reduce(
    (result, [name, value]) => result.replaceAll(`{{${name}}}`, value),
    current,
  )
}

const ERROR_CODE_KEYS = {
  not_found: 'errors.code.not_found',
  unauthorized: 'errors.code.unauthorized',
  forbidden: 'errors.code.forbidden',
  rate_limited: 'errors.code.rate_limited',
  conflict: 'errors.code.conflict',
  internal_error: 'errors.code.internal_error',
  validation_error: 'errors.code.validation_error',
  field_owned_by_hr: 'errors.code.field_owned_by_hr',
  salary_locked_by_pay_run: 'errors.code.salary_locked_by_pay_run',
  payroll_identity_locked_for_edit: 'errors.code.payroll_identity_locked_for_edit',
  client_not_active_for_assignment: 'errors.code.client_not_active_for_assignment',
  workforce_role_code_exists: 'errors.code.workforce_role_code_exists',
  unknown: 'errors.code.unknown',
} as const

type KnownErrorCode = keyof typeof ERROR_CODE_KEYS

function isKnownErrorCode(value: string): value is KnownErrorCode {
  return value in ERROR_CODE_KEYS
}

/** Short support reference from W3C trace id (e.g. REF-7F3A9C). */
export function toReference(id: string | undefined): string | undefined {
  if (!id) return undefined
  const hex = id.replace(/[^0-9a-fA-F]/g, '')
  return hex.length ? `REF-${hex.slice(-6).toUpperCase()}` : undefined
}

export interface ResolvedApiError {
  message: string
  reference?: string
  errorCode?: string
  status?: number
}

function stripGenericConflictTitle(extracted: string, fallbackMessage: string): string {
  const text = extracted.replace(/^Conflict:\s*/i, '').trim()
  if (!text || text === fallbackMessage || /^conflict$/i.test(text)) return ''
  return text
}

export function resolveApiError(
  error: unknown,
  t: TranslationFn,
  fallback?: string,
): ResolvedApiError {
  const { status, traceId, errorCode } = getApiErrorDetails(error)
  const reference = toReference(traceId)

  const fallbackMessage = fallback ?? t('errors.code.unknown')
  const extracted = extractApiErrorMessage(error, fallbackMessage)
  const codeKey =
    errorCode && isKnownErrorCode(errorCode) ? ERROR_CODE_KEYS[errorCode] : ERROR_CODE_KEYS.unknown
  const localized = t(codeKey)
  const conflictDetail =
    errorCode === 'conflict' ? stripGenericConflictTitle(extracted, fallbackMessage) : ''

  // Prefer the API sentence when it explains the conflict. Known codes otherwise stay localized.
  const message = conflictDetail
    ? conflictDetail
    : errorCode === 'validation_error' && extracted !== fallbackMessage
      ? extracted
      : errorCode && isKnownErrorCode(errorCode) && localized !== codeKey
        ? localized
        : extracted !== fallbackMessage
          ? extracted
          : localized !== codeKey
            ? localized
            : extracted

  const resolved: ResolvedApiError = { message }
  if (reference) resolved.reference = reference
  if (errorCode) resolved.errorCode = errorCode
  if (status !== undefined) resolved.status = status
  return resolved
}

/** User-facing text with optional REF- support id (toasts and inline banners). */
export function formatApiErrorText(
  error: unknown,
  fallback: string,
  t: TranslationFn = defaultT,
): string {
  const { message, reference } = resolveApiError(error, t, fallback)
  return reference ? `${message} (${t('errors.reference', { ref: reference })})` : message
}

/** Non-Axios errors (render failures, etc.). */
export function resolveUnknownError(error: unknown, t: TranslationFn): ResolvedApiError {
  if (error instanceof Error && error.message.trim()) {
    return { message: error.message }
  }
  return { message: t('errors.code.unknown') }
}

export function isAxiosApiError(error: unknown): error is AxiosError {
  return isAxiosError(error)
}
